/**
 * Sistema de Logging HMAC - Arquivos separados por action
 * 
 * Gera logs detalhados de comunicação HMAC para debug:
 * - Payload completo enviado
 * - Canonical body gerado
 * - Signature HMAC
 * - Erros e respostas do backend
 * - Arquivos .log separados por action
 * 
 * @module HmacLogger
 */

import { getLogLevel, LogLevel } from '@/config/logging';
// Sanitização de dados - Importada do módulo centralizado de segurança
import { sanitizeData as sanitizeDataCore } from '../../../security/sanitizers/data.sanitizer';

/**
 * Estrutura de um log HMAC
 */
export interface HmacLogEntry {
  timestamp: string;
  action: string;
  requestId?: string;
  type: 'request' | 'response' | 'error' | 'hmac_generation';
  data: {
    // Para request
    payload?: any;
    canonicalBody?: string;
    canonicalBodyLength?: number;
    signature?: string;
    nonce?: string;
    timestamp?: number;
    payloadHMAC?: string; // nonce|timestamp|action|canonicalBody
    
    // Para response
    response?: any;
    status?: string;
    error?: string;
    errorDetails?: any;
    
    // Para hmac_generation
    businessData?: any;
    hmacSecretAvailable?: boolean;
  };
}

/**
 * Gerenciador de logs HMAC
 */
class HmacLogger {
  private logs: Map<string, HmacLogEntry[]> = new Map();
  private maxLogsPerAction = 100; // Limite de logs por action
  private enabled = true;
  private storageKey = 'hmac_logs'; // Chave para localStorage/IndexedDB
  private autoDownloadOnError = false; // Auto-download de .log quando houver erro
  private autoDownloadKey = 'hmac_logs_auto_download'; // Chave para salvar preferência

  /**
   * Habilita ou desabilita o logging
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Habilita ou desabilita auto-download de .log quando houver erro
   */
  setAutoDownloadOnError(enabled: boolean): void {
    this.autoDownloadOnError = enabled;
    try {
      localStorage.setItem(this.autoDownloadKey, String(enabled));
      console.log(`✅ [HMAC_LOGGER] Auto-download em erro: ${enabled ? 'HABILITADO' : 'DESABILITADO'}`);
    } catch (error) {
      console.warn('[HMAC_LOGGER] Erro ao salvar preferência de auto-download:', error);
    }
  }

  /**
   * Verifica se auto-download está habilitado
   */
  isAutoDownloadOnErrorEnabled(): boolean {
    return this.autoDownloadOnError;
  }

  /**
   * Carrega preferência de auto-download do localStorage
   * Público para permitir inicialização externa
   */
  loadAutoDownloadPreference(): void {
    try {
      const stored = localStorage.getItem(this.autoDownloadKey);
      if (stored !== null) {
        this.autoDownloadOnError = stored === 'true';
      }
    } catch (error) {
      // Ignorar erro
    }
  }

  /**
   * Carrega logs salvos do localStorage
   */
  async loadFromStorage(): Promise<void> {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        for (const [action, logs] of Object.entries(parsed)) {
          this.logs.set(action, logs as HmacLogEntry[]);
        }
        console.log(`✅ [HMAC_LOGGER] Carregados ${this.logs.size} actions do storage`);
      }
    } catch (error) {
      console.error('[HMAC_LOGGER] Erro ao carregar logs do storage:', error);
    }
  }

  /**
   * Salva logs no localStorage (persistência)
   */
  private saveToStorage(): void {
    try {
      const toStore: Record<string, HmacLogEntry[]> = {};
      for (const [action, logs] of this.logs.entries()) {
        toStore[action] = logs;
      }
      localStorage.setItem(this.storageKey, JSON.stringify(toStore));
    } catch (error) {
      // Se exceder limite do localStorage, apenas logar erro
      console.warn('[HMAC_LOGGER] Erro ao salvar no storage (pode estar cheio):', error);
    }
  }

  /**
   * Limpa logs do storage
   */
  clearStorage(): void {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('✅ [HMAC_LOGGER] Storage limpo');
    } catch (error) {
      console.error('[HMAC_LOGGER] Erro ao limpar storage:', error);
    }
  }

  /**
   * Verifica se está habilitado
   */
  isEnabled(): boolean {
    return this.enabled && getLogLevel() >= LogLevel.DEBUG;
  }

  /**
   * Adiciona um log de geração de HMAC
   */
  logHmacGeneration(
    action: string,
    businessData: any,
    canonicalBody: string,
    signature: string,
    nonce: string,
    timestamp: number,
    hmacSecretAvailable: boolean
  ): void {
    if (!this.isEnabled()) return;

    const entry: HmacLogEntry = {
      timestamp: new Date().toISOString(),
      action,
      type: 'hmac_generation',
      data: {
        businessData: this.sanitizeData(businessData),
        canonicalBody,
        canonicalBodyLength: canonicalBody.length,
        signature,
        nonce,
        timestamp,
        payloadHMAC: `${nonce}|${timestamp}|${action}|${canonicalBody}`,
        hmacSecretAvailable
      }
    };

    this.addLog(action, entry);
  }

  /**
   * Adiciona um log de request
   */
  logRequest(
    action: string,
    requestId: string,
    payload: any
  ): void {
    if (!this.isEnabled()) return;

    const entry: HmacLogEntry = {
      timestamp: new Date().toISOString(),
      action,
      requestId,
      type: 'request',
      data: {
        payload: this.sanitizeData(payload)
      }
    };

    this.addLog(action, entry);
  }

  /**
   * Adiciona um log de response
   */
  logResponse(
    action: string,
    requestId: string,
    response: any
  ): void {
    if (!this.isEnabled()) return;

    const entry: HmacLogEntry = {
      timestamp: new Date().toISOString(),
      action,
      requestId,
      type: 'response',
      data: {
        response: this.sanitizeData(response),
        status: response?.status || response?.type || 'unknown'
      }
    };

    this.addLog(action, entry);
  }

  /**
   * Adiciona um log de erro
   */
  logError(
    action: string,
    requestId: string | undefined,
    error: string,
    errorDetails?: any,
    payload?: any
  ): void {
    if (!this.isEnabled()) return;

    const entry: HmacLogEntry = {
      timestamp: new Date().toISOString(),
      action,
      requestId,
      type: 'error',
      data: {
        error,
        errorDetails: this.sanitizeData(errorDetails),
        payload: payload ? this.sanitizeData(payload) : undefined
      }
    };

    this.addLog(action, entry);

    // Auto-download se habilitado
    if (this.autoDownloadOnError) {
      // Aguardar um pouco para garantir que o log foi adicionado
      setTimeout(() => {
        this.performAutoDownloadOnError(action, error);
      }, 500);
    }
  }

  /**
   * Faz download automático do arquivo .log quando houver erro
   */
  private performAutoDownloadOnError(action: string, error: string): void {
    try {
      // Verificar novamente se está habilitado (pode ter sido desabilitado)
      if (!this.autoDownloadOnError) {
        console.log('[HMAC_LOGGER] Auto-download desabilitado, pulando download');
        return;
      }

      const logs = this.getLogs(action);
      if (logs.length === 0) {
        console.warn(`[HMAC_LOGGER] Nenhum log encontrado para auto-download: ${action}`);
        return;
      }

      // Criar nome de arquivo com timestamp para evitar sobrescrever
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `${action}_ERROR_${timestamp}.log`;

      // Adicionar informação sobre o erro no início do arquivo
      let logContent = `=== ERRO DETECTADO - AUTO-DOWNLOAD ===\n`;
      logContent += `Action: ${action}\n`;
      logContent += `Erro: ${error}\n`;
      logContent += `Timestamp: ${new Date().toISOString()}\n`;
      logContent += `Total de logs: ${logs.length}\n`;
      logContent += `Auto-download habilitado: ${this.autoDownloadOnError}\n\n`;
      logContent += this.formatLogsForFile(action, logs);

      this.downloadFile(filename, logContent);
      console.log(`📥 [HMAC_LOGGER] Arquivo ${filename} baixado automaticamente devido a erro`);
      console.log(`📥 [HMAC_LOGGER] Action: ${action}, Erro: ${error}, Logs: ${logs.length}`);
    } catch (err) {
      console.error('[HMAC_LOGGER] Erro ao fazer auto-download:', err);
    }
  }

  /**
   * Adiciona log à lista (com limite)
   */
  private addLog(action: string, entry: HmacLogEntry): void {
    if (!this.logs.has(action)) {
      this.logs.set(action, []);
    }

    const actionLogs = this.logs.get(action)!;
    actionLogs.push(entry);

    // Limitar quantidade de logs por action
    if (actionLogs.length > this.maxLogsPerAction) {
      actionLogs.shift(); // Remove o mais antigo
    }

    // Salvar no localStorage para persistência
    this.saveToStorage();

    // Também logar no console para debug imediato
    this.logToConsole(entry);
  }

  /**
   * Loga no console para debug imediato
   */
  private logToConsole(entry: HmacLogEntry): void {
    const prefix = `[HMAC_LOG:${entry.action.toUpperCase()}]`;
    
    switch (entry.type) {
      case 'hmac_generation':
        console.group(`${prefix} HMAC Generation`);
        console.log('Canonical Body:', entry.data.canonicalBody);
        console.log('Signature:', entry.data.signature);
        console.log('Payload HMAC:', entry.data.payloadHMAC);
        console.log('Business Data:', entry.data.businessData);
        console.groupEnd();
        break;
      
      case 'request':
        console.group(`${prefix} Request`);
        console.log('RequestId:', entry.requestId);
        console.log('Payload:', entry.data.payload);
        console.groupEnd();
        break;
      
      case 'response':
        console.group(`${prefix} Response`);
        console.log('RequestId:', entry.requestId);
        console.log('Status:', entry.data.status);
        console.log('Response:', entry.data.response);
        console.groupEnd();
        break;
      
      case 'error':
        console.error(`${prefix} Error`, {
          requestId: entry.requestId,
          error: entry.data.error,
          errorDetails: entry.data.errorDetails,
          payload: entry.data.payload
        });
        break;
    }
  }

  /**
   * Sanitiza dados removendo informações sensíveis
   * Mantém estrutura mas remove valores sensíveis
   */
  /**
   * Sanitiza dados removendo campos sensíveis
   * 
   * @deprecated Use import direto de '@/lib/security/sanitizers/data.sanitizer'
   * Esta função é mantida apenas para compatibilidade com código legado.
   */
  private sanitizeData(data: any): any {
    return sanitizeDataCore(data);
  }

  /**
   * Obtém todos os logs de uma action
   */
  getLogs(action: string): HmacLogEntry[] {
    return this.logs.get(action) || [];
  }

  /**
   * Obtém todas as actions que têm logs
   */
  getActions(): string[] {
    return Array.from(this.logs.keys());
  }

  /**
   * Limpa logs de uma action específica
   */
  clearLogs(action: string): void {
    this.logs.delete(action);
    this.saveToStorage(); // Atualizar storage
  }

  /**
   * Limpa todos os logs
   */
  clearAllLogs(): void {
    this.logs.clear();
    this.clearStorage(); // Limpar também do storage
  }

  /**
   * Exporta logs de uma action para arquivo .log
   */
  exportLogsToFile(action: string): void {
    const logs = this.getLogs(action);
    if (logs.length === 0) {
      console.warn(`[HMAC_LOGGER] Nenhum log encontrado para action: ${action}`);
      return;
    }

    const logContent = this.formatLogsForFile(action, logs);
    this.downloadFile(`${action}.log`, logContent);
  }

  /**
   * Exporta todos os logs para arquivo único
   */
  exportAllLogsToFile(): void {
    const allLogs: Array<{ action: string; logs: HmacLogEntry[] }> = [];
    
    for (const action of this.getActions()) {
      const logs = this.getLogs(action);
      if (logs.length > 0) {
        allLogs.push({ action, logs });
      }
    }

    if (allLogs.length === 0) {
      console.warn('[HMAC_LOGGER] Nenhum log encontrado');
      return;
    }

    let content = '=== HMAC LOGS - TODAS AS ACTIONS ===\n';
    content += `Gerado em: ${new Date().toISOString()}\n\n`;

    for (const { action, logs } of allLogs) {
      content += this.formatLogsForFile(action, logs);
      content += '\n\n' + '='.repeat(80) + '\n\n';
    }

    this.downloadFile('hmac-all-actions.log', content);
  }

  /**
   * Formata logs para arquivo .log
   */
  private formatLogsForFile(action: string, logs: HmacLogEntry[]): string {
    let content = `=== HMAC LOGS - ACTION: ${action} ===\n`;
    content += `Total de logs: ${logs.length}\n`;
    content += `Gerado em: ${new Date().toISOString()}\n\n`;

    for (let i = 0; i < logs.length; i++) {
      const entry = logs[i];
      content += `\n--- Log ${i + 1}/${logs.length} ---\n`;
      content += `Timestamp: ${entry.timestamp}\n`;
      content += `Type: ${entry.type}\n`;
      
      if (entry.requestId) {
        content += `RequestId: ${entry.requestId}\n`;
      }

      content += '\n';

      switch (entry.type) {
        case 'hmac_generation':
          content += 'HMAC Generation Details:\n';
          content += `  Canonical Body Length: ${entry.data.canonicalBodyLength}\n`;
          content += `  Canonical Body: ${entry.data.canonicalBody}\n`;
          content += `  Signature: ${entry.data.signature}\n`;
          content += `  Nonce: ${entry.data.nonce}\n`;
          content += `  Timestamp: ${entry.data.timestamp}\n`;
          content += `  Payload HMAC: ${entry.data.payloadHMAC}\n`;
          content += `  HMAC Secret Available: ${entry.data.hmacSecretAvailable}\n`;
          content += '\nBusiness Data:\n';
          content += JSON.stringify(entry.data.businessData, null, 2);
          break;

        case 'request':
          content += 'Request Payload:\n';
          content += JSON.stringify(entry.data.payload, null, 2);
          break;

        case 'response':
          content += `Response Status: ${entry.data.status}\n`;
          content += 'Response Data:\n';
          content += JSON.stringify(entry.data.response, null, 2);
          break;

        case 'error':
          content += `Error: ${entry.data.error}\n`;
          if (entry.data.errorDetails) {
            content += 'Error Details:\n';
            content += JSON.stringify(entry.data.errorDetails, null, 2);
          }
          if (entry.data.payload) {
            content += '\nPayload (quando erro ocorreu):\n';
            content += JSON.stringify(entry.data.payload, null, 2);
          }
          break;
      }

      content += '\n';
    }

    return content;
  }

  /**
   * Faz download de arquivo .log
   */
  private downloadFile(filename: string, content: string): void {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Obtém estatísticas dos logs
   */
  getStats(): {
    totalActions: number;
    totalLogs: number;
    logsPerAction: Record<string, number>;
    errorsPerAction: Record<string, number>;
  } {
    const stats = {
      totalActions: this.logs.size,
      totalLogs: 0,
      logsPerAction: {} as Record<string, number>,
      errorsPerAction: {} as Record<string, number>
    };

    for (const [action, logs] of this.logs.entries()) {
      stats.logsPerAction[action] = logs.length;
      stats.totalLogs += logs.length;
      
      const errorCount = logs.filter(log => log.type === 'error').length;
      if (errorCount > 0) {
        stats.errorsPerAction[action] = errorCount;
      }
    }

    return stats;
  }
}

// Instância singleton
export const hmacLogger = new HmacLogger();

// Expor globalmente para debug
if (typeof window !== 'undefined') {
  (window as any).__hmacLogger = hmacLogger;
  
  // Carregar preferências e logs salvos ao inicializar
  hmacLogger.loadAutoDownloadPreference();
  hmacLogger.loadFromStorage().catch(err => {
    console.warn('[HMAC_LOGGER] Erro ao carregar logs do storage:', err);
  });
  
  // Adicionar helpers úteis
  (window as any).__hmacLogger.help = () => {
    console.log(`
🔍 HMAC Logger - Comandos Disponíveis:

📊 Estatísticas:
  __hmacLogger.getStats()                    - Ver estatísticas gerais
  __hmacLoggerHelpers.showHmacLoggerStats()  - Mostrar estatísticas formatadas

📄 Exportar Logs:
  __hmacLogger.exportLogsToFile('action')    - Exportar logs de uma action
  __hmacLogger.exportAllLogsToFile()         - Exportar todos os logs
  __hmacLoggerHelpers.exportCriticalActionLogs() - Exportar actions críticas

🔍 Consultar Logs:
  __hmacLogger.getLogs('action')             - Obter logs de uma action
  __hmacLogger.getActions()                  - Listar todas as actions com logs
  __hmacLoggerHelpers.checkRecentErrors(5)    - Verificar erros recentes (últimos 5 min)

🧹 Limpar Logs:
  __hmacLogger.clearLogs('action')           - Limpar logs de uma action
  __hmacLogger.clearAllLogs()                - Limpar todos os logs
  __hmacLoggerHelpers.clearCriticalActionLogs() - Limpar actions críticas

⚙️  Configuração:
  __hmacLogger.setEnabled(true/false)        - Habilitar/desabilitar logging
  __hmacLogger.isEnabled()                   - Verificar se está habilitado

💡 Actions Críticas:
  - salvararquivo
  - corel-export-batch
  - corel-export-simple
  - vbcompiler
  - execute-vb-code
  - corel:run-gms

💾 Persistência (localStorage):
  __hmacLogger.loadFromStorage()            - Carregar logs salvos do localStorage
  __hmacLogger.clearStorage()               - Limpar logs do localStorage

📥 Auto-Download em Erro:
  __hmacLogger.setAutoDownloadOnError(true)  - Habilitar auto-download quando houver erro
  __hmacLogger.setAutoDownloadOnError(false) - Desabilitar auto-download
  __hmacLogger.isAutoDownloadOnErrorEnabled() - Verificar se está habilitado
  
  ⚠️ IMPORTANTE: Por padrão está DESABILITADO. Você precisa habilitar manualmente!
    `);
  };
}

